import { exerciseHistoryOutput, exerciseRecordsOutput, exerciseRecordsPath, MAX_HISTORY_EXERCISE_IDS, quickWorkoutOutput, sessionEndOutput, sessionEndPath, sessionFeedbackPath, sessionListOutput, sessionPath, sessionSetsOutput, sessionSetsPath, sessionSorenessPath, sessionStarted, sessionSummary, sessionSummaryPath, setCorrected, setPath, setRecorded, TRAINING_ROUTES, toExerciseHistoryParams, trainingStats, workoutSessionView, } from "@repo/contract/training";
import { getJson, sendJson, sendNoContent, withQuery, } from "../core/request";
/**
 * The workout routes, one function each: path from the contract's constants
 * and builders, shape from its schemas, nothing restated.
 *
 * ── EVERY WRITE BODY GOES THROUGH UNCHANGED ─────────────────────────────────
 * The input is the contract's own type and is serialized whole — never picked
 * field by field. That is what lets the client-minted `id` and `startedAt` of
 * a start, and the `endedAt` and `endReason` of an end (`D19`, `D20`, `D42`,
 * T26), reach the server with no edit here, and what lets the next field the
 * contract grows do the same.
 *
 * ── THE OUTBOX CALLS THESE, SO NOTHING HERE RETRIES ─────────────────────────
 * `startSession`, `endSession`, `recordSet`, `correctSet` and `removeSet` are
 * plain functions so the offline outbox (T21, T53) can replay them without a
 * `MutationDef`. A `POST` is safe to resend only because the server is
 * idempotent by the client's id — resending is the outbox's decision, made
 * with that id in hand, and a retry here would be a second, blind one (T-90).
 */
/** `GET /me/sessions?from&to` — the history, `from`/`to` as calendar days,
 *  both inclusive and both optional. An inverted range is a 422, not `[]`. */
export function fetchSessions(api, range = {}, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.sessions, { from: range.from, to: range.to }), sessionListOutput, init);
}
/** `GET /me/sessions/:id` — one session with its visible sets. 404 for an
 *  unknown id or someone else's session. */
export function fetchSession(api, sessionId, init) {
    return getJson(api, sessionPath(sessionId), workoutSessionView, init);
}
/**
 * `GET /me/sessions/:id/summary` — the summary of an ENDED session, computed
 * by the same function `/end` answers with (T26). An open session is 409
 * `workout.session_not_ended`; an unknown or foreign id is 404. `changes` and
 * `nextSessionChanges` are `[]` here, exactly as on a replayed end.
 */
export function getSessionSummary(api, sessionId, init) {
    return getJson(api, sessionSummaryPath(sessionId), sessionSummary, init);
}
/**
 * `GET /me/sessions/:id/sets?locale` — that session's visible sets, oldest
 * first, each with its exercise's localized name (`D11`).
 *
 * The read behind correcting a set AFTER the workout. It is a SEPARATE call
 * from {@link fetchSession}, which carries the same rows in `set_number`
 * order and without names: a screen that has let go of the workout store has
 * only an `exerciseId`, and that is not a label anybody can find a set by.
 *
 * A superseded or removed set is already gone (`D53`), so every row here is
 * one {@link correctSet} can still act on. Someone else's or an unknown id is
 * 404 `workout.session_not_found`.
 */
export function fetchSessionSets(api, sessionId, options = {}, init) {
    return getJson(api, withQuery(sessionSetsPath(sessionId), { locale: options.locale }), sessionSetsOutput, init);
}
/**
 * `POST /me/sessions` — start a bout. With the client's `id` the start is
 * replayable: a resend answers the stored session with the same 200 and opens
 * nothing (`D19`). Without one, a second start while this is open is 409
 * `workout.session_already_open`.
 */
export function startSession(api, input) {
    return sendJson(api, "POST", TRAINING_ROUTES.sessions, input, sessionStarted);
}
/**
 * `POST /me/quick-workout?locale` — "Treino rápido" (`D249`): the engine's
 * session for today. A POST only because it has a body: it writes nothing,
 * and the same body answers the same session. Starting it is
 * {@link startSession} with `planSessionId: null`.
 */
export function composeQuickWorkout(api, input, options = {}) {
    return sendJson(api, "POST", withQuery(TRAINING_ROUTES.quickWorkout, { locale: options.locale }), input, quickWorkoutOutput);
}
/**
 * `POST /me/sessions/:id/end` — end it, and get the summary back.
 *
 * ── `changed: false` IS AN ANSWER, NOT AN ERROR ─────────────────────────────
 * Ending an ended session answers 200 with `changed: false`, the ORIGINAL
 * `endedAt` and the summary. It is returned as it came: the summary screen
 * needs it, and the outbox replaying an end it never saw answered must see
 * success. `{}` is a valid input — the server's clock, no reason given.
 */
export function endSession(api, sessionId, input = {}) {
    return sendJson(api, "POST", sessionEndPath(sessionId), input, sessionEndOutput);
}
/** `POST /me/sessions/:id/sets` — log one set. A set may be recorded into an
 *  ended session (`workout.ts`'s header says why). */
export function recordSet(api, sessionId, input) {
    return sendJson(api, "POST", sessionSetsPath(sessionId), input, setRecorded);
}
/** `PUT /me/sets/:id` — a correction: a NEW row supersedes this one and keeps
 *  its number (`D53`). Omitted fields carry forward; `null` clears. */
export function correctSet(api, setId, input) {
    return sendJson(api, "PUT", setPath(setId), input, setCorrected);
}
/** `DELETE /me/sets/:id` — a `204`. */
export function removeSet(api, setId) {
    return sendNoContent(api, "DELETE", setPath(setId));
}
/** `PUT /me/sessions/:id/feedback` — the four questions after a session.
 *  Every field is required by the contract. A `204`. */
export function submitSessionFeedback(api, sessionId, input) {
    return sendNoContent(api, "PUT", sessionFeedbackPath(sessionId), input);
}
/** `PUT /me/sessions/:id/soreness` — the next-morning check-in (`D100`).
 *  A `204`. */
export function submitSoreness(api, sessionId, input) {
    return sendNoContent(api, "PUT", sessionSorenessPath(sessionId), input);
}
/**
 * `GET /me/exercise-history?exerciseIds&from&to` — the chart behind `D104`,
 * oldest first. The ids are ONE comma-joined parameter, and the contract's
 * `toExerciseHistoryParams` is the only thing that joins them.
 *
 * ── MORE THAN 20 IDS IS SPLIT, NOT TRIMMED ──────────────────────────────────
 * The route takes at most `MAX_HISTORY_EXERCISE_IDS` ids and answers 422 above
 * that, so a longer list goes out as consecutive chunks of 20, in parallel,
 * each carrying the same `from`/`to` and the caller's `signal`. The chunks
 * never overlap, so the answers are concatenated and re-sorted oldest first —
 * the contract's order — with `setId` breaking a tie for a stable one. A list
 * of 20 or fewer, or none, is exactly one request, unchanged.
 */
export async function fetchExerciseHistory(api, range = {}, init) {
    const ids = range.exerciseIds ?? [];
    if (ids.length <= MAX_HISTORY_EXERCISE_IDS) {
        return await getExerciseHistoryChunk(api, range, init);
    }
    const chunks = [];
    for (let at = 0; at < ids.length; at += MAX_HISTORY_EXERCISE_IDS) {
        chunks.push(ids.slice(at, at + MAX_HISTORY_EXERCISE_IDS));
    }
    const answers = await Promise.all(chunks.map((exerciseIds) => getExerciseHistoryChunk(api, { ...range, exerciseIds }, init)));
    return { sets: answers.flatMap((answer) => answer.sets).sort(oldestFirst) };
}
function getExerciseHistoryChunk(api, range, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.exerciseHistory, toExerciseHistoryParams(range)), exerciseHistoryOutput, init);
}
function oldestFirst(left, right) {
    if (left.performedAt !== right.performedAt) {
        return left.performedAt < right.performedAt ? -1 : 1;
    }
    if (left.setId === right.setId) {
        return 0;
    }
    return left.setId < right.setId ? -1 : 1;
}
/** `GET /me/stats` — streak, this week, total sessions, milestones (`D152`).
 *  Server-computed: the app's history window cannot see a longer streak. */
export function fetchTrainingStats(api, init) {
    return getJson(api, TRAINING_ROUTES.stats, trainingStats, init);
}
/**
 * `GET /me/exercise-records/:exerciseId` — one exercise's all-time records,
 * rep maxes and one point per session (`D199`), for the exercise sheet's
 * "Recordes" and "Gráficos". Server-computed: the device would otherwise
 * need every set of the exercise ever logged.
 */
export function fetchExerciseRecords(api, exerciseId, init) {
    return getJson(api, exerciseRecordsPath(exerciseId), exerciseRecordsOutput, init);
}
//# sourceMappingURL=api.js.map